import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  Grid,
  Stack,
  MenuItem,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  ListItemText,
  Checkbox,
  CircularProgress,
  Chip,
  Typography,
  IconButton,
  DialogTitle,
  InputAdornment,
  Divider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import widgetApi from '../api/WidgetApi';
import s3Api from '../api/S3Api';
import iPhoneImage from '../resources/home_body.jpg';
import iPhoneHeader from '../resources/iphone-header.jpg';
import { useSnackbar } from 'notistack';

import { CloudUpload } from '@mui/icons-material';
import {
    DAYS_OF_WEEK_MAP,
    DEEP_LINKS_BASE_URL,
    BANNERS_FILTER_METHODS_MAP,
    BANNERS_ACTION_TYPE_MAP,
    IDS_TYPE_MAP_BY_ACTION_TYPE,
    PLATFORMS_MAP
} from '../constants';
import CloseIcon from '@mui/icons-material/Close';
  
const useStyles = makeStyles((theme) => ({
  highlight: {
    animation: `$highlight 1s`,
  },
  imageContainer: {
    height: '155px',
    position: 'relative',
    overflow: 'hidden',
  },
  '@keyframes highlight': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.07)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
}));

function parseUrlToJson(url) {
  const result = {};

  if (url.includes(DEEP_LINKS_BASE_URL)) {
      const urlObj = new URL(url);
      const queryParams = new URLSearchParams(urlObj.search);
    
      for (const [key, value] of queryParams.entries()) {
        // Si el campo es brand_id y es un string numérico, se convierte a un número entero
        if (key === 'brand_id' && !isNaN(value) && Number.isInteger(Number(value))) {
          result[key] = Number(value);
        } else {
          result[key] = value;
        }
      }
  }

  return result;
}

function parseJsonToUrl(json) {
  const urlObj = new URL(DEEP_LINKS_BASE_URL);
  const queryParams = new URLSearchParams();

  for (const [key, value] of Object.entries(json)) {
    // Si el campo es brand_id y es un número, se añade como un string
    if (key === 'brand_id' && typeof value === 'number') {
      queryParams.append(key, value.toString());
    } else {
      queryParams.append(key, value);
    }
  }

  urlObj.search = queryParams.toString();

  return urlObj.toString();
}

const Banner = ({ open, onUpdate, onClose, widget, token, countryCode }) => {
    const [errors, setErrors] = useState({ startHour: "", endHour: "" });
    const [selectedWidget, setSelectedWidget] = useState(null);
    const classes = useStyles();
    const [selectedDays, setSelectedDays] = useState(widget.param.days);
    const [image, setImageLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tags, setTags] = useState(widget.param.user_tags || []);
    const [excludeUserTags, setExcludeUserTags] = useState(widget.param.exclude_user_tags || []);
    const [cityIds, setCityIds] = useState(widget.city_ids || []);
    const [mzIds, setMZIds] = useState(widget.microzone_ids || []);
    const [ids, setIds] = useState(widget.param.ids || []);
    const [userIds, setUserIds] = useState(widget.param.user_ids || []);
    const [enabled, setEnabled] = useState(widget.enabled);
    const [filterMethod, setFilterMethod] = useState(widget.param.filter_method);
    const [actionType, setActionType] = useState(widget.data[0].additional_information.content_type || 'deeplink');
    const [listsType, setListsType] = useState(widget.param.ids_type);
    const [r8Image, setR8Image] = useState(widget.image || '');

    const [bannerVersion, setBannerVersion] = useState(() => {
      const hasR7 = widget.data[0].image;
      const hasR8 = widget.image;
      if (hasR7 && hasR8) return 'ALL';
      if (!hasR7 && hasR8) return 'R8_ONLY';
      if (hasR7 && !hasR8) return 'R7_ONLY';
      return 'ALL';
    });


    const { enqueueSnackbar } = useSnackbar();



    const campaignIdRef = useRef();
    const daysRef = useRef();
    const startDateRef = useRef();
    const endDateRef = useRef();
    const startHourRef = useRef();
    const elementsToShowRef = useRef();
    const maxItemsPerBrandRef = useRef();
    const endHourRef = useRef();
    const enabledRef = useRef();
    const filterMethodRef = useRef();
    const actionTypeRef = useRef();
    const listsTypeRef = useRef();
    const completeDeeplinkRef = useRef();
    const ignoreCoverageRef = useRef();
    const deepLinkRef = useRef();
    const userTagsRef = useRef();
    const excludeUserTagsRef = useRef();
    const cityIdsRef = useRef();
    const mzIdsRef = useRef();
    const idsRef = useRef();
    const userIdsRef = useRef();
    const priorityRef = useRef();
    const deeplinkPlatformRef = useRef();


    useEffect(() => {
        setSelectedWidget(widget);
    }, [widget]);

  const handleSubmit = async () => {
    try {

      const currentActionType = actionTypeRef.current.value;
      const r7ImageIsInvalid = !isValidImage(widget.data[0].image);
      const r8ImageIsInvalid = !isValidImage(r8Image);
      let parsedDeepLink = {};

      if (deepLinkRef.current && deepLinkRef.current.value) {
        parsedDeepLink = parseUrlToJson(deepLinkRef.current.value);
      }

      if (bannerVersion === 'ALL' && (r7ImageIsInvalid || r8ImageIsInvalid)) {
        enqueueSnackbar('Error: The banner must have a valid R7 & R8 image', { variant: 'error' });
      }

      if (bannerVersion === 'R8_ONLY' && r8ImageIsInvalid) {
        enqueueSnackbar('Error: The banner must have a valid R8 image', { variant: 'error' });
      }

      if (bannerVersion === 'R7_ONLY' && r7ImageIsInvalid) {
        enqueueSnackbar('Error: The banner must have a valid R7 image', { variant: 'error' });
      }

      if (!areAllIntegers(cityIds)) {
        enqueueSnackbar('Error: The city ids field must contain only integer numbers.', { variant: 'error' });
        return;
      }

      if (!areAllIntegers(mzIds)) {
        enqueueSnackbar('Error: The microzone ids field must contain only integer numbers.', { variant: 'error' });
        return;
      }

      if (currentActionType === 'deeplink' && Object.keys(parsedDeepLink).length === 0) {
        enqueueSnackbar('Error: Deep link field cannot be empty', { variant: 'error' });
        return;
      }
  
      if ((currentActionType === 'stores' || currentActionType === 'products') && (ids.length == 0 || listsType == '')) {
        enqueueSnackbar('Error: Both Lists and Lists Type fields must be filled', { variant: 'error' });
        return;
      }

      if ((currentActionType === 'stores' || currentActionType === 'products') && !isValidImage(widget.data[0].additional_information.secondary_image)) {
        enqueueSnackbar('Error: The in-app must have a valid image', { variant: 'error' });
        return;
      }
      
      const updatedWidget = {
        ...widget,
        image: r8Image || '',
        enabled: enabledRef.current.value,
        city_ids: cityIds,
        microzone_ids: mzIds,
        data: [
            {
                ...widget.data[0],
                special: ignoreCoverageRef.current?.checked ?? false,
                additional_information: {
                    ...widget.data[0].additional_information,
                    content_type: actionTypeRef.current.value,
                    deep_link: parsedDeepLink,
                    banner_id: campaignIdRef.current.value,
                    max_items_by_brand: parseInt(maxItemsPerBrandRef.current?.value) ?? 3,
                },
            }
        ],
        param: {
            ...widget.param,
            filter_method: filterMethodRef.current.value,
            days: daysRef.current.value,
            start_hour: startHourRef.current.value,
            end_hour: endHourRef.current.value,
            start_date: startDateRef.current.value,
            end_date: endDateRef.current.value,
            user_ids: userIds,
            segmentation_ids: [],
            user_tags: tags,
            exclude_user_tags: excludeUserTags,
            ids: ids,
            ids_type: listsType
        },
        elements_to_show: elementsToShowRef.current?.value ?? 1,
        additional_information: {
            ...widget.additional_information,
            complete_deeplink: completeDeeplinkRef.current?.checked ?? false,
            complete_deeplink_platform: deeplinkPlatformRef.current?.value ?? 'ALL',
            priority: parseInt(priorityRef.current.value, 10)
        },
      }

      if (bannerVersion === 'R7_ONLY') {
        updatedWidget.image = '';
      } else if (bannerVersion === 'R8_ONLY') {
        updatedWidget.data[0].image = '';
      }

      const response = await widgetApi.updateWidget(countryCode, updatedWidget, token)  
      enqueueSnackbar('Banner saved successfully', { variant: 'success' });

      onUpdate(response);
      onClose();
   
    } catch (error) {
      console.log("ERR:",error)
      let errorMessage = 'Error updating banner';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += ': ' + error.response.data.message;
      } else {
        errorMessage += ': An unexpected error occurred';
      }
    
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const isValidImage = (imageUrl) => {
    return typeof imageUrl === 'string' && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'));
  };

  const areAllIntegers = (array) => {
    return array.every(element => Number.isInteger(Number(element)));
  }

  const handleImageUpload = async (event) => {
    setLoading(true);
    const file = event.target.files[0];
    if (!file) { return; }
    
    const newImageUrl = await s3Api.uploadImage(countryCode, file, token)

    setSelectedWidget((prevWidget) => {
        const newData = [...prevWidget.data];
        newData[0].image = newImageUrl;
  
        return {
          ...prevWidget,
          data: newData,
        };
      });
      enqueueSnackbar('banner image uploaded successfully', { variant: 'success' });
      setLoading(false);
  };

  const handleR8ImageUpload = async (event) => {
    setLoading(true);
    try {
        const file = event.target.files[0];
        if (!file) return;

        const newImageUrl = await s3Api.uploadImage(countryCode, file, token);

        setR8Image(newImageUrl); 
        enqueueSnackbar('R8 banner image uploaded successfully', { variant: 'success' });
    } catch (error) {
        console.error('Image upload failed:', error);
        enqueueSnackbar('Error uploading R8 image', { variant: 'error' });
    } finally {
        setLoading(false);
    }
};


  const handleInappImageUpload = async (event) => {
    setLoading(true);
    const file = event.target.files[0];
    if (!file) { return; }
    
    const newImageUrl = await s3Api.uploadImage(countryCode, file, token)

    setSelectedWidget((prevWidget) => {
        const newData = [...prevWidget.data];
        newData[0].additional_information.secondary_image = newImageUrl;
  
        return {
          ...prevWidget,
          data: newData,
        };
      });
      enqueueSnackbar('Inapp image uploaded successfully', { variant: 'success' });
      setLoading(false);
  };

  const handleDaysChange = (event) => {
    setSelectedDays(event.target.value);
    daysRef.current.value = event.target.value;
  };

  const handleTagKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setTags([...tags, event.target.value.trim()]);
      userTagsRef.current.value = '';
    }
  };

  const handleExcludeUserTagKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setExcludeUserTags([...excludeUserTags, event.target.value.trim()]);
      excludeUserTagsRef.current.value = '';
    }
  };

  const handleCityIdKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setCityIds([...cityIds, event.target.value.trim()]);
      cityIdsRef.current.value = '';
    }
  };

  const handleMZIdKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setMZIds([...mzIds, event.target.value.trim()]);
      mzIdsRef.current.value = '';
    }
  };

  const handleListKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setIds([...ids, event.target.value.trim()]);
      idsRef.current.value = '';
    }
  };

  const handleDeleteTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleDeleteExcludeUserTag = (tagToRemove) => {
    setExcludeUserTags(excludeUserTags.filter((tag) => tag !== tagToRemove));
  };

  const handleDeleteCityId = (cityIdToRemove) => {
    setCityIds(cityIds.filter((cityId) => cityId !== cityIdToRemove));
  };

  const handleDeleteMZId = (mzIdToRemove) => {
    setMZIds(mzIds.filter((mzId) => mzId !== mzIdToRemove));
  };

  const handleDeleteList = (listToRemove) => {
    setIds(ids.filter((list) => list !== listToRemove));
  };

  const handleKeyDownUserId = (event) => {
    if (event.key === 'Enter' && event.target.value.trim() !== '') {
      setUserIds([...userIds, event.target.value.trim()]);
      userIdsRef.current.value = '';
    }
  };

  const handleDeleteUserId = (userIdToRemove) => {
    setUserIds(userIds.filter((userId) => userId !== userIdToRemove));
  };

  const handleSpecialSwitchChange = (event) => {
    ignoreCoverageRef.current = event.target.checked;
  };

  const handleChipClick = (id) => {
    navigator.clipboard.writeText(id).then(() => {
      enqueueSnackbar('List name copied to the clipboard.', { variant: 'default' });
    }).catch(err => {
      console.error("Error al copiar el texto al portapapeles", err);
    });
  };
  

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle 
          style={{
            backgroundColor: widget.enabled ? '#e8f5e9' : '#d1d1d1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,  // Hace la letra más "gordita"
            fontFamily: 'Poppins, sans-serif',  // Usa la fuente "Poppins"
            fontSize: '1.2rem',  // Aumenta el tamaño si es necesario
            padding: '5.5px 20px',
        }}
      >
          {widget.enabled ? 'This banner is enabled' : 'This banner is disabled'}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={5}>
          <Grid item xs={12} md={6} style={{ marginTop: "5px" }}>
            <TextField
                label="ID"
                value={widget.id}
                margin="dense"
                disabled
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            edge="end"
                            onClick={() => {
                              navigator.clipboard.writeText(widget.id);
                              enqueueSnackbar('Banner ID copied to the clipboard.', { variant: 'default' });
                          }}
                        >
                            <FileCopyIcon fontSize="small"/>
                        </IconButton>
                    </InputAdornment>
                  ),
                }}
            />
            <TextField
              label="Campaign ID"
              defaultValue={widget.data[0].additional_information.banner_id}
              inputProps={{ ref: campaignIdRef }}
              margin="dense"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                      <IconButton
                          edge="end"
                          onClick={() => {
                              navigator.clipboard.writeText(widget.data[0].additional_information.banner_id);
                              enqueueSnackbar('Campaign ID copied to the clipboard.', { variant: 'default' });
                          }}
                      >
                          <FileCopyIcon fontSize="small"/>
                      </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Grid item xs={12} style={{ marginTop: '7px' }}>
              <ToggleButtonGroup
                value={bannerVersion}
                exclusive
                onChange={(event, newVersion) => {
                  if (newVersion !== null) setBannerVersion(newVersion);
                }}
                aria-label="Selección de versión"
                style={{
                  display: 'flex',
                  width: '100%',
                }}
              >
                <ToggleButton 
                  value="R8_ONLY" 
                  aria-label="R8 ONLY"
                  style={{ flex: 1 }}
                >
                  R8 ONLY
                </ToggleButton>
                <ToggleButton 
                  value="R7_ONLY" 
                  aria-label="R7 ONLY"
                  style={{ flex: 1 }}
                >
                  R7 ONLY
                </ToggleButton>
    
                <ToggleButton
                  value="ALL"
                  aria-label="R7 & R8"
                  style={{ flex: 1 }}
                >
                  R7 & R8
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            {bannerVersion !== 'R7_ONLY' && (
            <Grid item xs={6} style={{ marginTop: "7px" }}>
              <input
                accept="image/png"
                id="upload-r8-image"
                type="file"
                style={{ display: 'none' }}
                onChange={handleR8ImageUpload}
              />
              <label htmlFor="upload-r8-image">
                <Button
                  variant="outlined"
                  color="primary"
                  component="span"
                  startIcon={<CloudUpload />}
                  style={{
                    height: '50px',
                    width: '407px', 
                  }}
                >
                  Update R8 banner image
                </Button>
              </label>
            </Grid>
            )}
            {bannerVersion !== 'R8_ONLY' && (
            <Grid item xs={6} style={{ marginTop: "7px" }}>
              <input
                accept="image/png"
                id="upload-image"
                type="file"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
              <label htmlFor="upload-image">
                <Button
                  variant="outlined"
                  color="primary"
                  component="span"
                  startIcon={<CloudUpload />}
                  style={{
                    height: '50px',
                    width: '407px', 
                  }}
                >
                  Update R7 banner image
                </Button>
              </label>
            </Grid>
            )}
            {actionType !== 'deeplink' && (
            <Grid item xs={6} style={{ marginTop: "7px" }}>
              <input
                accept="image/png"
                id="upload-inapp-image"
                type="file"
                style={{ display: 'none' }}
                onChange={handleInappImageUpload}
              />
              <label htmlFor="upload-inapp-image">
                <Button
                  variant="outlined"
                  color="primary"
                  component="span"
                  startIcon={<CloudUpload />}
                  style={{
                    height: '50px',
                    width: '407px',
                    borderColor: '#808080', 
                    color: '#808080' 
                  }}
                >
                  Update in-app image
                </Button>
              </label>
            </Grid>
            )}
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  defaultValue={widget.param.start_date}
                  inputProps={{ ref: startDateRef }}
                  style={{
                    minWidth: '195px', 
                  }}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                </Grid>
                <Grid item xs={6}>
                <TextField
                  label="End date"
                  margin="normal"
                  type="date"
                  inputProps={{ ref: endDateRef }}
                  defaultValue={widget.param.end_date}
                  style={{
                    minWidth: '195px', 
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                </Grid>
            </Grid>
            <FormControl fullWidth margin="dense">
              <InputLabel  shrink={true} htmlFor="days-select">Days</InputLabel>
              <Select
                id="days-select"
                label="Days"
                multiple
                defaultValue={widget.param.days}
                inputProps={{ ref: daysRef }}
                onChange={handleDaysChange}
                renderValue={(selected) =>
                  selected.map((day) => DAYS_OF_WEEK_MAP[day]).join(', ')
                }
              >
                {Object.keys(DAYS_OF_WEEK_MAP).map((day) => (
                  <MenuItem key={day} value={day}>
                    <Checkbox checked={selectedDays.includes(day)} />
                    <ListItemText primary={DAYS_OF_WEEK_MAP[day]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        label="Start hour"
                        defaultValue={widget.param.start_hour}
                        inputProps={{ ref: startHourRef }}
                        margin="dense"
                        fullWidth
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="End hour"
                        defaultValue={widget.param.end_hour}
                        inputProps={{ ref: endHourRef }}
                        margin="dense"
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>

            <FormControl fullWidth margin="dense">
              <InputLabel shrink={true} htmlFor="action-select">Action</InputLabel>
              <Select
                id="action-select"
                label="Action"
                onChange={(e) => setActionType(e.target.value)}
                defaultValue={widget.data[0].additional_information.content_type || 'deeplink'}
                inputProps={{ ref: actionTypeRef }}
              >
                {Object.keys(BANNERS_ACTION_TYPE_MAP).map((method) => (
                  <MenuItem key={method} value={method}>
                    <ListItemText primary={BANNERS_ACTION_TYPE_MAP[method]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {actionType === 'deeplink' && (
            <TextField
              label="Deeplink"
              defaultValue={parseJsonToUrl(widget.data[0].additional_information.deep_link)}
              inputProps={{ ref: deepLinkRef }}
              margin="normal"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                      <IconButton
                          edge="end"
                          onClick={() => {
                              navigator.clipboard.writeText(parseJsonToUrl(widget.data[0].additional_information.deep_link));
                              enqueueSnackbar('Deeplink copied to the clipboard.', { variant: 'default' });
                          }}
                      >
                          <FileCopyIcon fontSize="small"/>
                      </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            )}
            {actionType === 'deeplink' && (
            <Grid container spacing={2}>
                <Grid item xs={6}>
                <FormControlLabel
                    control={
                      <Switch
                          defaultChecked={widget.data[0].special}
                          inputProps={{ ref: ignoreCoverageRef }}
                          color="primary"
                      />
                  }
                  label="Ignore stores coverage"
                />
                </Grid>
                <Grid item xs={6}>
                    <FormControlLabel
                    control={
                        <Switch
                            defaultChecked={!!widget.additional_information.complete_deeplink}
                            inputProps={{ ref: completeDeeplinkRef }}
                            color="primary"
                        />
                    }
                    label="Complete deeplink"
                    />
                </Grid>
            </Grid>
            )}

          {actionType === 'deeplink' && (
            <FormControl fullWidth margin="dense">
              <InputLabel shrink={true} htmlFor="deeplink-platform-select">Complete deeplink platform</InputLabel>
              <Select
                id="deeplink-platform-select"
                label="Complete deeplink platform"
                defaultValue={widget.additional_information.complete_deeplink_platform || 'ALL'}
                inputProps={{ ref: deeplinkPlatformRef }}
              >
                {Object.keys(PLATFORMS_MAP).map((method) => (
                  <MenuItem key={method} value={method}>
                    <ListItemText primary={PLATFORMS_MAP[method]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
            {actionType !== 'deeplink' && (
            <FormControl fullWidth margin="dense">
                <InputLabel shrink={true} htmlFor="list-type-select">List Type</InputLabel>
                <Select
                  id="list-type-select"
                  label="Lists Type"
                  defaultValue={widget.param.ids_type}
                  onChange={(e) => setListsType(e.target.value)}
                  inputProps={{ ref: listsTypeRef }}
                >
                  {Object.keys(IDS_TYPE_MAP_BY_ACTION_TYPE[actionType]).map((method) => (
                    <MenuItem key={method} value={method}>
                      <ListItemText primary={IDS_TYPE_MAP_BY_ACTION_TYPE[actionType][method]} />
                    </MenuItem>
                  ))}
                </Select>
            </FormControl>
            )}
            {actionType !== 'deeplink' && (
              <>
                {ids.map((id) => (
                  <Chip
                    key={id}
                    label={id}
                    onClick={() => handleChipClick(id)}
                    onDelete={() => handleDeleteList(id)}
                    style={{ marginRight: '5px', marginBottom: '5px' }}
                  />
                ))}
                <TextField
                  inputRef={idsRef}
                  label="Lists"
                  onKeyDown={handleListKeyDown}
                  placeholder="Add list"
                  style={{
                    minWidth: '195px', 
                  }}
                  margin="normal"
                  fullWidth
                />
              </>
            )}
            {actionType !== 'deeplink' && (
              <TextField
                label="Min elements to show"
                defaultValue={widget.elements_to_show}
                inputProps={{ ref: elementsToShowRef }}
                margin="dense"
                fullWidth
              />
            )}
            {actionType !== 'deeplink' && (
              <TextField
                label="Maximum number of items per brand"
                defaultValue={widget.data[0]?.additional_information?.max_items_by_brand || 3}
                inputProps={{ ref: maxItemsPerBrandRef }}
                margin="dense"
                fullWidth
              />
            )}
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>
            <div style={{ marginTop: '16px' }}>
            <Typography variant="subtitle1" style={{ color: 'gray', fontSize: '12px' }}>User tags</Typography>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleDeleteTag(tag)}
                style={{ marginRight: '5px', marginBottom: '5px' }}
              />
            ))}
            <TextField
              inputRef={userTagsRef}
              onKeyDown={handleTagKeyDown}
              placeholder="Add tag"
              margin="dense"
              fullWidth
            />
            <Typography variant="subtitle1" style={{ color: 'gray', fontSize: '12px' }}>Exclude user tags</Typography>
            {excludeUserTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleDeleteExcludeUserTag(tag)}
                style={{ marginRight: '5px', marginBottom: '5px' }}
              />
            ))}
            <TextField
              inputRef={excludeUserTagsRef}
              onKeyDown={handleExcludeUserTagKeyDown}
              placeholder="Add tag"
              margin="dense"
              fullWidth
            />
            <Typography variant="subtitle1" style={{ color: 'gray', fontSize: '12px' }}>City Ids</Typography>
            {cityIds.map((cityId) => (
              <Chip
                key={cityId}
                label={cityId}
                onDelete={() => handleDeleteCityId(cityId)}
                style={{ marginRight: '5px', marginBottom: '5px' }}
              />
            ))}
            <TextField
              inputRef={cityIdsRef}
              onKeyDown={handleCityIdKeyDown}
              placeholder="Add city Id"
              margin="dense"
              fullWidth
            />
            <Typography variant="subtitle1" style={{ color: 'gray', fontSize: '12px' }}>Microzone Ids</Typography>
            {mzIds.map((mzId) => (
              <Chip
                key={mzId}
                label={mzId}
                onDelete={() => handleDeleteMZId(mzId)}
                style={{ marginRight: '5px', marginBottom: '5px' }}
              />
            ))}
            <TextField
              inputRef={mzIdsRef}
              onKeyDown={handleMZIdKeyDown}
              placeholder="Add microzone Id"
              margin="dense"
              fullWidth
            />
          <Typography variant="subtitle1" style={{ color: 'gray', fontSize: '12px' }}>Users ID (for test)</Typography>
            {userIds.map((userId) => (
              <Chip
                key={userId}
                label={userId}
                onDelete={() => handleDeleteUserId(userId)}
                style={{ marginRight: '5px', marginBottom: '5px' }}
              />
            ))}

            <TextField
              inputRef={userIdsRef}
              onKeyDown={handleKeyDownUserId}
              placeholder="Add User ID"
              margin="dense"
              fullWidth
            />
            <Grid item xs={12} style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Divider />
            </Grid>

            <FormControl fullWidth margin="dense">
              <InputLabel shrink={true} htmlFor="vertical-select">Priority</InputLabel>
              <Select
                id="vertical-select"
                label="Vertical"
                defaultValue={widget.param.filter_method}
                inputProps={{ ref: filterMethodRef }}
              >
                {Object.keys(BANNERS_FILTER_METHODS_MAP).map((method) => (
                  <MenuItem key={method} value={method}>
                    <ListItemText primary={BANNERS_FILTER_METHODS_MAP[method]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
              


<Grid container spacing={2}>
<Grid item xs={6}>
    <FormControl fullWidth margin="normal">
      <InputLabel htmlFor="status-select">
        Status
      </InputLabel>
      <Select
        id="status-select"
        defaultValue={widget.enabled}
        inputProps={{ ref: enabledRef }}
        label="Status"
      >
        <MenuItem value={true}>
  
          On
        </MenuItem>
        <MenuItem value={false}>
 
          Off
        </MenuItem>
      </Select>
    </FormControl>
  </Grid>
<Grid item xs={6}>
    <TextField
      label="Priority Rank"
      defaultValue={widget.additional_information.priority}
      inputProps={{ ref: priorityRef }}
      margin="normal"
      fullWidth
    />
  </Grid>
</Grid>
        </Grid>
        <Grid item xs={12} md={6}>
            <Stack direction="column" spacing={1}>
                <img
                src={iPhoneHeader}
                alt="Widget"
                style={{
                    maxWidth: '100%',
                    maxHeight: '30%',
                    objectFit: 'contain',
                }}
                />
              <div className={classes.imageContainer} 
                  style={{ height: '310px', position: 'relative', overflow: 'hidden' }}
              >
                {loading ? (
                  <CircularProgress style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }} />
                ) : (
                  <img
                    src={r8Image}
                    alt="Banner"
                    className={classes.highlight}
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '12px'
                    }}
                  />
                )}
              </div>
            <Grid item xs={12} style={{ marginTop: '40px', marginBottom: '40px' }}>
              <Divider />
            </Grid>
            {actionType !== 'deeplink' && (
              <img
                src={widget.data[0].additional_information.secondary_image}
                alt="inapp"
                style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                }}
              />
            )}
            {(bannerVersion === 'R7_ONLY' || bannerVersion === 'ALL') && (
                <img
                  src={widget.data[0].image}
                  alt="R7 banner"
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                  }}
                />
              )}
            {actionType !== 'deeplink' && (
              <img
                src={widget.data[0].additional_information.secondary_r8_image}
                alt="inapp"
                style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                }}
              />
            )}
            </Stack>
        </Grid>
        </Grid>
        </DialogContent>
        <DialogActions>
  <Grid container spacing={2}>
    <Grid item xs={3}>
      <Button
        onClick={onClose}
        fullWidth
        style={{ backgroundColor: 'gray', color: 'white', width: 'calc(100% - 12px)', marginLeft: '15px' }}
      >
        Cancel
      </Button>
    </Grid>
    <Grid item xs={3}>
      <Button
        onClick={handleSubmit}
        fullWidth
        style={{ backgroundColor: '#00A86C', color: 'white', width: 'calc(100% - 12px)' }}
      >
        Save
      </Button>
    </Grid>

    <Grid item xs={6}></Grid> {/* Espacio vacío que ocupa la mitad del ancho restante */}
  </Grid>
</DialogActions>

    </Dialog>
    );
  };

export default Banner;